// frontend/src/main.tsx - Updated for new API architecture
// MODIFIED FUNCTION: Updated imports and error boundary integration

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ConfirmationProvider } from './components/ui/ConfirmationDialog';
import { InputDialogProvider } from './components/ui/InputDialog';
import './index.css';

// Add global error handling for unhandled promises and errors
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  // Optionally report to error tracking service
});

window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
  // Optionally report to error tracking service
});

// Enhanced error boundary for root level
class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('Root Error Boundary:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Root Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Application Error
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Something went wrong. Please refresh the page or contact support if the problem persists.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Reload Application
            </button>
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto max-h-40">
                  {this.state.error?.stack || this.state.error?.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root')!);

root.render(
  <React.StrictMode>
    <RootErrorBoundary>
      <BrowserRouter>
        <ConfirmationProvider>
          <InputDialogProvider>
            <App />
          </InputDialogProvider>
        </ConfirmationProvider>
      </BrowserRouter>
    </RootErrorBoundary>
  </React.StrictMode>
);